import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, Users, BookOpen, Compass, AlertCircle } from 'lucide-react';
import { CONTENT_TYPES } from '../../../../../utils/whatVervestacks/inferContentType';

function hasProse(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function hasList(value) {
  return Array.isArray(value) && value.length > 0;
}

/** YAML `|` blocks often wrap at ~80 cols; join those lines, keep blank-line paragraphs. */
export function proseParagraphs(text) {
  return text
    .trim()
    .split(/\n\s*\n/)
    .map((block) => block.replace(/\s*\n\s*/g, ' ').trim())
    .filter(Boolean);
}

export function ProseBlock({ text }) {
  if (!hasProse(text)) return null;
  const paragraphs = proseParagraphs(text);
  return (
    <div className="w-full min-w-0 text-sm md:text-base text-gray-700 leading-relaxed space-y-3">
      {paragraphs.map((paragraph, index) => (
        <p key={index} className="w-full">
          {paragraph}
        </p>
      ))}
    </div>
  );
}

export function ScenarioDimensionsBlock({ dimensions }) {
  if (!hasList(dimensions)) return null;

  return (
    <div className="grid w-full min-w-0 gap-3 sm:gap-4 sm:grid-cols-2">
      {dimensions.map((dim, index) => (
        <div
          key={`${dim.name}-${index}`}
          className="rounded-lg border border-gray-200 bg-gray-50/80 p-3 sm:p-4"
        >
          <h4 className="text-sm font-semibold text-gray-900 mb-2">{dim.name}</h4>
          {hasList(dim.states) && (
            <ul className="space-y-1">
              {dim.states.map((state, i) => (
                <li
                  key={i}
                  className="text-xs sm:text-sm text-gray-600 flex items-start gap-2"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
                  <span>{state}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

export function ExpertsBlock({ experts }) {
  if (!hasList(experts)) return null;

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {experts.map((expert) => (
        <div key={expert.id || expert.name} className="card p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg shrink-0">
              <Users className="h-4 w-4 text-indigo-600" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-gray-900 text-sm md:text-base">
                {expert.name}
              </div>
              {expert.affiliation && (
                <div className="text-xs sm:text-sm text-gray-600 mt-0.5">
                  {expert.affiliation}
                </div>
              )}
              {expert.role && (
                <div className="text-xs text-indigo-600 mt-1 capitalize">
                  {String(expert.role).replace(/_/g, ' ')}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function RecommendedViewsBlock({ views }) {
  if (!hasList(views)) return null;

  const hasTeasers = views.some((view) => hasProse(view.teaser));

  if (hasTeasers) {
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        {views.map((view) => (
          <div
            key={view.id || view.label}
            className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-3 sm:p-4"
          >
            <div className="flex items-start gap-2">
              <Compass className="h-4 w-4 text-indigo-600 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-indigo-900">{view.label}</div>
                {view.teaser && (
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">{view.teaser}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <ul className="flex flex-wrap gap-2">
      {views.map((view) => (
        <li
          key={view.id || view.label}
          className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs sm:text-sm font-medium text-indigo-800"
        >
          <Compass className="h-3.5 w-3.5 shrink-0" />
          {view.label}
        </li>
      ))}
    </ul>
  );
}

export function ReferencesBlock({ references }) {
  if (!hasList(references)) return null;

  return (
    <ul className="space-y-3">
      {references.map((ref) => (
        <li key={ref.id || ref.title} className="flex items-start gap-3 text-sm">
          <BookOpen className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium text-gray-900">{ref.title}</div>
            {(ref.publisher || ref.year) && (
              <div className="text-gray-600 text-xs sm:text-sm mt-0.5">
                {[ref.publisher, ref.year].filter(Boolean).join(' · ')}
              </div>
            )}
            {ref.note && (
              <p className="text-xs text-gray-500 mt-1">{ref.note}</p>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

export function OpenQuestionsBlock({ questions }) {
  if (!hasList(questions)) return null;

  return (
    <div className="space-y-4 md:space-y-5">
      {questions.map((item, index) => (
        <div
          key={item.question || index}
          className="rounded-lg border border-gray-200 bg-gray-50/80 p-3 sm:p-4"
        >
          <h4 className="text-sm md:text-base font-semibold text-gray-900">
            {item.question}
          </h4>
          {item.context && (
            <div className="mt-2">
              <ProseBlock text={item.context} />
            </div>
          )}
          {hasList(item.states) && (
            <ul className="mt-3 space-y-2">
              {item.states.map((state, i) => (
                <li
                  key={i}
                  className="text-xs sm:text-sm text-gray-700 flex flex-col sm:flex-row sm:gap-2 sm:items-baseline"
                >
                  <span className="font-medium text-gray-900 shrink-0">
                    {state.label}
                  </span>
                  {state.model_value && (
                    <span className="text-gray-500 font-mono text-xs">
                      {state.model_value}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

export function MethodologicalChoicesBlock({ choices }) {
  if (!hasList(choices)) return null;

  return (
    <div className="space-y-4">
      {choices.map((item, index) => (
        <div
          key={item.axis || index}
          className="rounded-lg border border-gray-200 bg-gray-50/80 p-3 sm:p-4"
        >
          <h4 className="text-sm font-semibold text-gray-900">{item.axis}</h4>
          {hasList(item.states) && (
            <ul className="mt-2 flex flex-wrap gap-2">
              {item.states.map((state, i) => (
                <li
                  key={i}
                  className="inline-flex rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-xs text-indigo-800"
                >
                  {state}
                </li>
              ))}
            </ul>
          )}
          {item.rationale && (
            <div className="mt-3">
              <ProseBlock text={item.rationale} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function FindingsBlock({ findings }) {
  if (hasProse(findings)) {
    return <ProseBlock text={findings} />;
  }
  if (!hasList(findings)) return null;
  return (
    <ul className="space-y-2 text-sm md:text-base text-gray-700 list-disc pl-5">
      {findings.map((item, i) => (
        <li key={i}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>
      ))}
    </ul>
  );
}

export function CountriesCoverageBlock({ countries }) {
  if (!countries) return null;

  const tiers = [
    {
      key: 'pace_available',
      label: 'PACE matrix available',
      accent: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    },
    {
      key: 'model_built',
      label: 'Model built',
      accent: 'text-blue-700 bg-blue-50 border-blue-200',
    },
    {
      key: 'pipeline_staged',
      label: 'Pipeline staged',
      accent: 'text-amber-700 bg-amber-50 border-amber-200',
    },
  ];

  const hasAny = tiers.some((t) => hasList(countries[t.key]));
  if (!hasAny) return null;

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-slate-50 to-indigo-50 flex items-center gap-2">
        <Globe className="h-5 w-5 text-indigo-600" />
        <h3 className="text-sm md:text-base font-semibold text-gray-900">
          VerveStacks country coverage
        </h3>
      </div>
      <div className="p-4 space-y-4">
        {tiers.map(({ key, label, accent }) => {
          const items = countries[key];
          if (!hasList(items)) return null;
          return (
            <div key={key}>
              <div
                className={`inline-block text-xs font-semibold px-2 py-0.5 rounded border mb-2 ${accent}`}
              >
                {label}
              </div>
              <ul className="space-y-2">
                {items.map((item) => (
                  <li key={item.iso} className="text-sm text-gray-700">
                    <span className="font-mono text-xs text-gray-500 mr-2">
                      {item.iso}
                    </span>
                    <span className="font-medium">{item.name}</span>
                    {item.country_expert && (
                      <span className="block text-xs text-gray-500 mt-0.5">
                        {item.country_expert}
                      </span>
                    )}
                    {item.note && (
                      <span className="block text-xs text-gray-500 mt-0.5">
                        {item.note}
                      </span>
                    )}
                    {item.matrix_size && (
                      <span className="block text-xs text-gray-500 mt-0.5">
                        {item.matrix_size}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SectionCard({ title, children }) {
  if (!children) return null;
  return (
    <section className="card overflow-hidden w-full min-w-0">
      <div className="px-4 md:px-5 py-3 md:py-4 border-b border-gray-200 bg-white">
        <h3 className="text-base md:text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="p-4 md:p-5 w-full min-w-0">{children}</div>
    </section>
  );
}

export function FrameworkCard({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="card overflow-hidden w-full min-w-0">
      <div className="px-4 md:px-5 py-3 md:py-4 border-b border-gray-200 bg-gradient-to-r from-violet-50 to-indigo-50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white rounded-lg shadow-sm">
            <Icon className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900">{title}</h3>
            {subtitle && (
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5">{subtitle}</p>
            )}
          </div>
        </div>
      </div>
      <div className="p-4 md:p-5">{children}</div>
    </div>
  );
}

export function PanelError({ message }) {
  return (
    <div className="card p-6 text-center">
      <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
      <p className="text-sm text-red-700">{message}</p>
    </div>
  );
}

export function PanelLoading() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      <span className="ml-3 text-sm text-gray-600">Loading…</span>
    </div>
  );
}

export function SectionContentRenderer({ contentType, payload }) {
  switch (contentType) {
    case CONTENT_TYPES.PROSE:
      return <ProseBlock text={payload} />;
    case CONTENT_TYPES.SCENARIO_DIMENSIONS:
      return <ScenarioDimensionsBlock dimensions={payload} />;
    case CONTENT_TYPES.OPEN_QUESTIONS:
      return <OpenQuestionsBlock questions={payload} />;
    case CONTENT_TYPES.METHODOLOGICAL_CHOICES:
      return <MethodologicalChoicesBlock choices={payload} />;
    case CONTENT_TYPES.EXPERTS:
      return <ExpertsBlock experts={payload} />;
    case CONTENT_TYPES.RECOMMENDED_VIEWS:
      return <RecommendedViewsBlock views={payload} />;
    case CONTENT_TYPES.REFERENCES:
      return <ReferencesBlock references={payload} />;
    case CONTENT_TYPES.FINDINGS:
      return <FindingsBlock findings={payload} />;
    default:
      return null;
  }
}

export function DynamicPanelSections({ sections }) {
  if (!sections?.length) return null;

  return (
    <>
      {sections.map((section) => {
        const body = section.hasContent ? (
          <SectionContentRenderer
            contentType={section.contentType}
            payload={section.payload}
          />
        ) : null;

        if (!body && !section.required) return null;

        return (
          <SectionCard key={section.id} title={section.title}>
            {body || (
              <p className="text-sm text-gray-500 italic">
                Content for this section is not yet available.
              </p>
            )}
          </SectionCard>
        );
      })}
    </>
  );
}

export function PanelCtaRow({ cta, presentation, simulatorHref, countryIso }) {
  const showSimulatorLink = presentation?.show_simulator_link !== false;

  if (!cta) return null;

  return (
    <div className="card p-4 md:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-gray-50">
      <div className="min-w-0">
        {cta.primary?.label && (
          <div className="font-semibold text-gray-900 text-sm md:text-base">
            {cta.primary.label}
          </div>
        )}
        {cta.primary?.description && (
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            {cta.primary.description}
          </p>
        )}
      </div>
      <div className="flex flex-wrap gap-2 shrink-0">
        {showSimulatorLink && countryIso && (
          <Link to={simulatorHref} className="btn-primary text-sm">
            Launch simulator
          </Link>
        )}
        {cta.secondary?.label && (
          <button
            type="button"
            className="btn-outline text-sm"
            disabled
            title="Coming soon"
          >
            {cta.secondary.label}
          </button>
        )}
      </div>
    </div>
  );
}
